import { useEffect, useState } from 'react';
import { Alert, Button, Form, InputNumber, Modal, Select, Space, Table, Tag, notification } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import {
    useLazyGetDepartmentClientsQuery,
    useAddDepartmentClientMutation,
    useUpdateDepartmentClientMutation,
    useDeleteDepartmentClientMutation,
} from '../../redux/api/departments';
import { incidentTypes, producerTypes, workflowTypes } from '../../util/constant';
import ClientSelect from '../../component/client-select';

interface DepartmentClientRow {
    id: number;
    clientId: number;
    clientName: string;
    departmentId: number;
    workflowType: string;
    producerType: string;
    incidentType: string;
}

const PAGE_SIZE = 25;

const extractErrorMessage = (e: any): string => {
    if (typeof e?.data === 'string' && e.data) return e.data;
    if (typeof e?.data?.message === 'string') return e.data.message;
    if (typeof e?.error === 'string') return e.error;
    return 'An unexpected error occurred.';
};

const DepartmentMappingPage = () => {

    const [mappings, setMappings] = useState<DepartmentClientRow[]>([]);
    const [page, setPage] = useState({ current: 1, pageSize: PAGE_SIZE, total: 0 });
    const [tableHeight, setTableHeight] = useState(0);
    const [isAddOpen, setAddOpen] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);
    const [isEditOpen, setEditOpen] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);
    const [editingRecord, setEditingRecord] = useState<DepartmentClientRow | null>(null);

    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const [modal, contextHolder] = Modal.useModal();

    const [getDepartmentClients] = useLazyGetDepartmentClientsQuery();
    const [postDepartmentClient] = useAddDepartmentClientMutation();
    const [putDepartmentClient] = useUpdateDepartmentClientMutation();
    const [removeDepartmentClient] = useDeleteDepartmentClientMutation();

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 72,
        },
        {
            title: 'Client ID',
            dataIndex: 'clientId',
            key: 'clientId',
            width: 96,
        },
        {
            title: 'Client',
            dataIndex: 'clientName',
            key: 'clientName',
            width: 160,
        },
        {
            title: 'Department ID',
            dataIndex: 'departmentId',
            key: 'departmentId',
            width: 136,
        },
        {
            title: 'Workflow Type',
            dataIndex: 'workflowType',
            key: 'workflowType',
            width: 160,
            render: (v: string) => v ? <Tag>{v.replace(/_/g, ' ')}</Tag> : '',
        },
        {
            title: 'Producer Type',
            dataIndex: 'producerType',
            key: 'producerType',
            width: 136,
            render: (v: string) => v ? <Tag>{v}</Tag> : '',
        },
        {
            title: 'Incident Type',
            dataIndex: 'incidentType',
            key: 'incidentType',
            width: 136,
            render: (v: string) => v ? <Tag>{v}</Tag> : '',
        },
        {
            title: 'Action',
            key: 'action',
            width: 160,
            render: (_: any, record: DepartmentClientRow) => (
                <Space>
                    <Button size="small" onClick={() => openEditModal(record)}>
                        Edit
                    </Button>
                    <Button danger size="small" onClick={() => onDelete(record)}>
                        Remove
                    </Button>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        const updateTableHeight = () => setTableHeight(window.innerHeight - 288);
        updateTableHeight();
        window.addEventListener('resize', updateTableHeight);
        return () => window.removeEventListener('resize', updateTableHeight);
    }, []);

    useEffect(() => {
        fetchMappings();
    }, [page.current, page.pageSize]);

    const fetchMappings = async () => {
        try {
            const data: any = await getDepartmentClients({ index: page.current, viewableSize: page.pageSize }).unwrap();
            setMappings(data.items.map((m: DepartmentClientRow) => ({ ...m, key: m.id })));
            setPage(prev => ({ ...prev, total: data.page.totalSize }));
        } catch (e) {
            notification.error({
                message: 'Failed to load mappings',
                description: 'An error occurred while fetching department mappings.',
            });
        }
    };

    const handleTableChange = (pagination: any) => {
        setPage(prev => ({
            ...prev,
            current: pagination.pageSize !== prev.pageSize ? 1 : pagination.current,
            pageSize: pagination.pageSize,
        }));
    };

    const openAddModal = () => {
        setAddError(null);
        setAddOpen(true);
    };

    const closeAddModal = () => {
        setAddOpen(false);
        setAddError(null);
        addForm.resetFields();
    };

    const addMapping = async (values: any) => {
        setAddError(null);
        try {
            await postDepartmentClient(values).unwrap();
            closeAddModal();
            fetchMappings();
            notification.success({
                message: 'Mapping Added',
                description: 'The department-to-client mapping has been created.',
            });
        } catch (e: any) {
            console.log(e);
            setAddError(extractErrorMessage(e));
        }
    };

    const openEditModal = (record: DepartmentClientRow) => {
        setEditError(null);
        setEditingRecord(record);
        editForm.setFieldsValue(record);
        setEditOpen(true);
    };

    const closeEditModal = () => {
        setEditOpen(false);
        setEditError(null);
        setEditingRecord(null);
        editForm.resetFields();
    };

    const editMapping = async (values: any) => {
        if (!editingRecord) return;
        setEditError(null);
        try {
            await putDepartmentClient({ id: editingRecord.id, ...values }).unwrap();
            closeEditModal();
            fetchMappings();
            notification.success({
                message: 'Mapping Updated',
                description: 'The department-to-client mapping has been updated.',
            });
        } catch (e: any) {
            setEditError(extractErrorMessage(e));
        }
    };

    const deleteMapping = async (record: DepartmentClientRow) => {
        try {
            await removeDepartmentClient(record.id).unwrap();
            fetchMappings();
            notification.success({
                message: 'Mapping Removed',
                description: `Mapping for department ${record.departmentId} has been removed.`,
            });
        } catch (e) {
            notification.error({
                message: 'Failed to Remove Mapping',
                description: 'An error occurred while removing the mapping.',
            });
        }
    };

    const onDelete = (record: DepartmentClientRow) => {
        modal.confirm({
            title: 'Remove Mapping',
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to remove the mapping for department ${record.departmentId}?`,
            okText: 'Remove',
            okType: 'primary',
            cancelText: 'Cancel',
            onOk: () => deleteMapping(record),
        });
    };

    return (
        <>
            {contextHolder}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1>Department Mapping</h1>
                <Space>
                    <Button type="primary" onClick={openAddModal}>
                        Add Mapping
                    </Button>
                </Space>
            </div>

            <Table
                dataSource={mappings}
                columns={columns}
                rowKey="id"
                scroll={{ y: tableHeight }}
                pagination={{
                    current: page.current,
                    pageSize: page.pageSize,
                    total: page.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '25', '50', '100'],
                    showTotal: (total) => `${total} mapping${total === 1 ? '' : 's'}`,
                }}
                onChange={handleTableChange}
            />

            <Modal
                title="Add Department Mapping"
                closable={false}
                open={isAddOpen}
                onOk={() => addForm.submit()}
                okType="primary"
                onCancel={closeAddModal}
                width={400}
                destroyOnClose={true}
            >
                {addError && (
                    <Alert
                        message={addError}
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
                <Form form={addForm} onFinish={addMapping} layout="vertical">
                    <Form.Item
                        label="Client"
                        name="clientId"
                        rules={[{ required: true, message: 'Client is required.' }]}
                    >
                        <ClientSelect />
                    </Form.Item>
                    <Form.Item
                        label="Department ID"
                        name="departmentId"
                        rules={[{ required: true, message: 'Department ID is required.' }]}
                    >
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        label="Workflow Type"
                        name="workflowType"
                        rules={[{ required: true, message: 'Workflow Type is required.' }]}
                    >
                        <Select options={workflowTypes} />
                    </Form.Item>
                    <Form.Item
                        label="Producer Type"
                        name="producerType"
                        rules={[{ required: true, message: 'Producer Type is required.' }]}
                    >
                        <Select options={producerTypes} />
                    </Form.Item>
                    <Form.Item
                        label="Incident Type"
                        name="incidentType"
                        rules={[{ required: true, message: 'Incident Type is required.' }]}
                    >
                        <Select options={incidentTypes} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Edit Department Mapping"
                closable={false}
                open={isEditOpen}
                onOk={() => editForm.submit()}
                okType="primary"
                onCancel={closeEditModal}
                width={400}
                destroyOnClose={true}
            >
                {editError && (
                    <Alert
                        message={editError}
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
                <Form form={editForm} onFinish={editMapping} layout="vertical">
                    <Form.Item
                        label="Client"
                        name="clientId"
                        rules={[{ required: true, message: 'Client is required.' }]}
                    >
                        <ClientSelect />
                    </Form.Item>
                    <Form.Item
                        label="Department ID"
                        name="departmentId"
                        rules={[{ required: true, message: 'Department ID is required.' }]}
                    >
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        label="Workflow Type"
                        name="workflowType"
                        rules={[{ required: true, message: 'Workflow Type is required.' }]}
                    >
                        <Select options={workflowTypes} />
                    </Form.Item>
                    <Form.Item
                        label="Producer Type"
                        name="producerType"
                        rules={[{ required: true, message: 'Producer Type is required.' }]}
                    >
                        <Select options={producerTypes} />
                    </Form.Item>
                    <Form.Item
                        label="Incident Type"
                        name="incidentType"
                        rules={[{ required: true, message: 'Incident Type is required.' }]}
                    >
                        <Select options={incidentTypes} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default DepartmentMappingPage;
